<script setup lang="ts">
import { computed } from 'vue';

type Variant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark'
  | 'mjml'
  | string;

const props = withDefaults(
  defineProps<{
    id?: string;
    text?: string | number | null;
    value?: string | number | null;
    variant?: Variant | null;
    block?: boolean;
    variantMap?: Record<string, Variant> | null;
    colorCode?: string | null;
    noBoxShadow?: boolean;
    class?: string | string[] | Record<string, boolean>;
  }>(),
  {
    id: null,
    text: null,
    value: null,
    variant: null,
    block: false,
    variantMap: null,
    colorCode: null,
    noBoxShadow: false,
    class: '',
  },
);

const resolvedVariant = computed<Variant | null>(() => {
  if (props.variantMap && props.value) {
    return props.variantMap[props.value] ?? null;
  }
  return props.variant ?? null;
});

const VARIANTS = new Set([
  'primary',
  'secondary',
  'success',
  'danger',
  'warning',
  'info',
  'light',
  'dark',
]);

const variantClasses = computed(() => {
  const value = resolvedVariant.value;
  if (!value) {
    return [];
  }
  if (VARIANTS.has(value)) {
    return [`text-bg-${value}`];
  }

  return [`badge-${value}`];
});

const className = computed(() => [
  'badge',
  'itp-badge',
  'd-inline-flex',
  'align-items-center',
  'justify-content-start',
  props.block && 'd-block',
  props.noBoxShadow && 'shadow-none',
  ...variantClasses.value,
  props.colorCode && `bg-${props.colorCode}`,
  props.class,
]);
</script>

<template>
  <span :id="id" :class="className">
    <slot>{{ text }}</slot>
  </span>
</template>
